import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Save, X, Upload, Download, Loader2 } from "lucide-react";
import { adminApi, API_BASE } from "@/lib/api";
import { toast } from "sonner";

export default function QA() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null); // null | 'new' | id
    const [form, setForm] = useState({ question: "", answer: "", category: "" });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = () => adminApi.get("/api/admin/qa").then((r) => setItems(r.data.items));
    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        if (!form.question.trim() || !form.answer.trim()) {
            toast.error("Both question and answer are required");
            return;
        }
        if (open === "new") await adminApi.post("/api/admin/qa", form);
        else await adminApi.patch(`/api/admin/qa/${open}`, form);
        toast.success("Saved");
        setOpen(null);
        setForm({ question: "", answer: "", category: "" });
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this Q&A pair?")) return;
        await adminApi.delete(`/api/admin/qa/${id}`);
        toast.success("Deleted");
        load();
    };

    const startEdit = (q) => {
        setOpen(q.id);
        setForm({ question: q.question, answer: q.answer, category: q.category || "" });
    };

    const downloadTemplate = async () => {
        const token = localStorage.getItem("orchid_admin_token");
        const res = await fetch(`${API_BASE}/api/admin/qa/template.csv`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "orchid_qa_template.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const onBulkFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await adminApi.post("/api/admin/qa/bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `Imported ${data.inserted} Q&A pair${data.inserted === 1 ? "" : "s"}${
                    data.skipped ? ` (${data.skipped} skipped)` : ""
                }`
            );
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="page-qa">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                        Knowledge Base
                    </div>
                    <h1 className="font-heading text-5xl text-orchid-700">Q&amp;A Pairs</h1>
                    <p className="text-stone-600 mt-2">
                        Hand-curated answers Priya will give to specific guest questions.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={onBulkFile}
                        className="hidden"
                        data-testid="qa-bulk-input"
                    />
                    <button
                        onClick={downloadTemplate}
                        data-testid="qa-template"
                        className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                    >
                        <Download size={14} /> CSV template
                    </button>
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="qa-bulk-upload"
                        className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-md border border-orchid-700 text-orchid-700 hover:bg-orchid-700 hover:text-white disabled:opacity-50"
                    >
                        {uploading ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Upload size={14} />
                        )}
                        {uploading ? "Importing…" : "Bulk upload"}
                    </button>
                    <button
                        onClick={() => {
                            setOpen("new");
                            setForm({ question: "", answer: "", category: "" });
                        }}
                        data-testid="qa-add"
                        className="flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2.5 rounded-md transition-colors"
                    >
                        <Plus size={15} /> New Q&amp;A
                    </button>
                </div>
            </div>

            {open && (
                <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6 space-y-4" data-testid="qa-editor">
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                            Question
                        </label>
                        <input
                            value={form.question}
                            onChange={(e) => setForm({ ...form, question: e.target.value })}
                            data-testid="qa-question"
                            placeholder="E.g. Do you offer airport pickup at The Orchid Mumbai?"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                            Answer
                        </label>
                        <textarea
                            rows={4}
                            value={form.answer}
                            onChange={(e) => setForm({ ...form, answer: e.target.value })}
                            data-testid="qa-answer"
                            placeholder="Detailed answer (short sentences, no rates)"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                            Category (optional)
                        </label>
                        <input
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            data-testid="qa-category"
                            placeholder="Bookings, Dining, Rewards, etc."
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={save}
                            data-testid="qa-save"
                            className="flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md"
                        >
                            <Save size={15} /> Save
                        </button>
                        <button
                            onClick={() => setOpen(null)}
                            className="flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                        >
                            <X size={15} /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-3" data-testid="qa-list">
                {items.map((q) => (
                    <div key={q.id} className="bg-white border border-stone-200 rounded-lg p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <div className="font-medium text-orchid-700 text-sm">{q.question}</div>
                                <div className="text-sm text-stone-700 mt-2 leading-relaxed whitespace-pre-wrap">
                                    {q.answer}
                                </div>
                                {q.category && (
                                    <span className="inline-block mt-3 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sage-500/15 text-sage-700">
                                        {q.category}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button
                                    onClick={() => startEdit(q)}
                                    className="text-xs text-orchid-700 hover:underline"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => remove(q.id)}
                                    className="p-1.5 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && !open && (
                    <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-500">
                        No Q&amp;A pairs yet. Click <strong>New Q&amp;A</strong> to add one, or use{" "}
                        <strong>Bulk upload</strong> for a CSV.
                    </div>
                )}
            </div>
        </div>
    );
}
